import React, { useContext, useState, useEffect, useRef } from 'react';
import { Context } from 'components/Store';
import { useHistory, useParams } from 'react-router-dom';
import { usePDF } from '@react-pdf/renderer';
import { invoice as invoiceApi } from 'utilities/api';
import catchCancel from 'helpers/catchCancel';
import { parseServerError } from 'helpers/errorHelpers';
import { format } from 'date-fns';
import usePDFTools from 'hooks/usePDFTools';
import useCompanies from 'hooks/useCompanies';
import InvoicePdf from 'components/PDF/InvoicePdf';
import { mapInvoiceProductsToTable } from 'components/PDF/InvoicePdf/helpers/tableMaker';

import { paths } from 'routes/paths';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';

import { Flow } from 'screens/Applications&WorkOrders/RouteAdmin';
import { OperationType } from 'components/RecordsListView/constants';
import { message } from 'antd';
import { RECORD_SOURCE_MESSAGE_KEY } from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab/components/RecordSourceButton/constants/messageKey';
import {
  defaultValues,
  fetchInvoiceById
} from '../Invoice/helpers/fetchInvoiceById';

const useInvoiceActions = () => {
  const amplitude = useContext(AmplitudeContext);
  const [{ organization }, dispatch] = useContext(Context);
  const { companies } = useCompanies();

  const { downloadPDFV2, downloading, setDownloading } = usePDFTools();
  const [invoiceToPdf, setInvoiceToPdf] = useState();
  // Held in a variable so the same element can be handed to updateInstance
  // below: usePDF only reads `document` on mount, and its updater renders
  // whatever it is passed rather than re-reading this prop.
  const pdfDocument = <InvoicePdf invoice={invoiceToPdf} />;

  const [instance, updateInstance] = usePDF({ document: pdfDocument });

  const prevPdfUrl = useRef(undefined);

  useEffect(() => {
    if (invoiceToPdf) {
      updateInstance(pdfDocument);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceToPdf]);

  useEffect(() => {
    if (
      !instance.loading &&
      instance.url &&
      prevPdfUrl.current !== instance.url &&
      invoiceToPdf
    ) {
      prevPdfUrl.current = instance.url;
      downloadPDFV2(prevPdfUrl.current, `Invoice - ${invoiceToPdf.title}.pdf`);
      setInvoiceToPdf(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance]);

  const [deleteModalState, setDeleteModalState] = useState({
    open: false,
    id: null
  });
  const [invoiceTags, setInvoiceTags] = useState([]);
  const [openTags, setOpenTags] = useState(false);
  const [selectedInvoiceTitleAndId, setSelectedInvoiceTitleAndId] = useState({
    id: '',
    title: ''
  });

  const [selectedInvoiceData, setSelectedInvoiceData] = useState(defaultValues);

  const history = useHistory();

  const viewAction = invoice => {
    history.push(`${paths.invoices}/${invoice.invoiceId}/view`);
  };

  const duplicateAction = invoice => {
    history.push({
      pathname: `${paths.invoices}/${invoice.invoiceId}/duplicate`,
      invoiceID: invoice.invoiceId,
      duplicate: true
    });
  };

  const editAction = invoice => {
    history.push({
      pathname: `${paths.invoices}/${invoice.invoiceId}`,
      invoiceID: invoice.invoiceId
    });
  };

  const deleteAction = invoice => {
    setDeleteModalState({ id: invoice.invoiceId, open: true });
  };

  const tagAction = invoice => {
    setInvoiceTags(
      invoice.flags
        ? invoice.flags.reduce((tagString, { id }) => {
            return `${tagString},${id}`;
          }, '')
        : ''
    );
    setSelectedInvoiceTitleAndId({
      id: invoice.invoiceId,
      title: invoice.title
    });
    setOpenTags(true);
    fetchInvoiceById(invoice.invoiceId, setSelectedInvoiceData);
  };

  const fetchInvoiceProducts = async id => {
    const { promise } = invoiceApi.fetch(id);

    const invoiceProducts = await promise
      .then(({ data }) => data.invoiceProducts)
      .catch(catchCancel)
      .catch(err => {
        parseServerError(dispatch)(err);

        return [];
      });

    return invoiceProducts;
  };

  const createApplicationAction = async invoice => {
    amplitude.sendEventToAmplitude(
      amplitude.events.epic.Invoices.initiateInvoiceToApplication
    );
    history.push(`${paths.applications}/create`, {
      flow: Flow.FROM_INVOICE_TO_OPERATION,
      targetOperation: OperationType.APPLICATION,
      invoice,
      returnPath: paths.invoices
    });
  };

  const createWorkOrderAction = async invoice => {
    amplitude.sendEventToAmplitude(
      amplitude.events.epic.Invoices.initiateInvoiceToWorkOrder
    );
    history.push(`${paths.workOrders}/create`, {
      flow: Flow.FROM_INVOICE_TO_OPERATION,
      targetOperation: OperationType.WORK_ORDER,
      invoice,
      returnPath: paths.invoices
    });
  };

  const getCompanyNameById = vendorId => {
    let vendorName = '--';
    if (companies?.length > 0 && vendorId !== '') {
      vendorName = companies.find(company => company.id === vendorId)?.name;
    }
    return vendorName || '--';
  };
  const { id } = useParams();
  const shareOrPrintAction = async invoice => {
    setDownloading(true);
    const vendorName = getCompanyNameById(invoice.vendor);
    const generatedDate = new Date();
    const year = format(generatedDate, 'yyyy');
    const fullDate = {
      english: format(generatedDate, 'MM/dd/yyyy h:mm aaa'),
      LATAMSpanish: format(generatedDate, 'dd/MM/yyyy h:mm aaaa')
    };
    const invoiceProducts = await fetchInvoiceProducts(invoice.invoiceId);
    const mappedInvoiceProductEntries = mapInvoiceProductsToTable(
      invoiceProducts
    );
    const invoiceDataToPrint = {
      ...invoice,
      vendorName,
      year,
      fullDate,
      organizationName: organization.name,
      mappedInvoiceProductEntries
    };
    if (!id) {
      // to check if this method is called from the landing page (id is undefined)
      const { promise } = await invoiceApi.fetch(invoice.invoiceId);
      promise
        .then(({ data }) => {
          if (!data) return;
          const {
            notes,
            invoiceDateEpoch,
            invoiceDueDateEpoch,
            cropSeasons
          } = data;
          setInvoiceToPdf({
            ...invoiceDataToPrint,
            notes,
            invoiceDateEpoch,
            invoiceDueDateEpoch,
            cropSeasons
          });
        })
        .catch(catchCancel);
    } else setInvoiceToPdf({ ...invoiceDataToPrint });
    setDownloading(false);
  };

  const handleAction = (
    action,
    invoice,
    isLanding = false,
    index,
    setIndex
  ) => {
    if (isLanding) setIndex(index);

    const actions = {
      view: viewAction,
      edit: editAction,
      delete: deleteAction,
      createApplication: createApplicationAction,
      createWorkOrder: createWorkOrderAction,
      shareOrPrint: () => shareOrPrintAction(invoice),
      downloadPDFEnglish: () => shareOrPrintAction(invoice),
      tag: tagAction,
      duplicate: duplicateAction
    };

    message.destroy(RECORD_SOURCE_MESSAGE_KEY);

    actions[action](invoice);
  };

  return {
    handleAction,
    deleteModalState,
    invoiceTags,
    openTags,
    selectedInvoiceTitleAndId,
    selectedInvoiceData,
    setOpenTags,
    setInvoiceTags,
    setDeleteModalState,
    shareOrPrint: shareOrPrintAction,
    downloadingPDF: downloading
  };
};

export default useInvoiceActions;
